import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { footerCopy } from "../locales/footer";
import BrandLogo from "./BrandLogo";
import { AnalyticsPreferencesButton } from "./Analytics";

const footerLink =
  "inline-flex min-h-[44px] items-center py-2 text-sm leading-5 text-slate-600 transition-colors hover:text-goszakup-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-goszakup-600";

export default function Footer() {
  const { language, t } = useLanguage();
  const copy = footerCopy[language];
  const groups = [
    {
      title: copy.services,
      links: [
        ["/services/lot-search", t("nav.lots")],
        ["/services/search-alerts", language === 'kk' ? 'Іздеуге жазылу' : 'Подписка на поиск'],
        ["/services/suppliers", t("nav.suppliers")],
        ["/services/lot-tracking", t("nav.tracker")],
        ["/services/financial-analysis", t("nav.calculator")],
        ["/financial-planning", t("nav.financialPlanning")],
        ["/services/experience", t("nav.experience")],
        ["/plans", t("nav.plans")],
      ],
    },
    {
      title: copy.site,
      links: [
        ["/", t("nav.main")],
        ["/about", t("nav.about")],
        ["/news", t("nav.news")],
        ["/questions", t("nav.questions")],
        ["/contacts", t("nav.contacts")],
      ],
    },
    {
      title: copy.documents,
      links: [
        ["/offer", copy.offer],
        ["/privacy", copy.privacy],
        ["/refund", copy.refund],
        ["/security", copy.security],
      ],
    },
  ];

  return (
    <footer
      lang={language}
      className="w-full border-t border-slate-200 bg-white text-slate-600 lg:pl-20"
    >
      <div className="mx-auto max-w-[1240px] px-[clamp(20px,4vw,56px)]">
        <div className="grid gap-x-10 gap-y-10 py-10 sm:grid-cols-2 lg:grid-cols-[1.3fr_1.1fr_.8fr_1fr] lg:py-14">
          <div className="min-w-0">
            <Link
              to="/"
              aria-label={`G-Launcher — ${t("nav.main")}`}
              className="mb-6 block w-fit rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-goszakup-600"
            >
              <BrandLogo
                variant="full"
                loading="lazy"
                alt=""
                className="h-24 w-auto"
              />
            </Link>
            <p className="text-sm font-semibold text-slate-900">
              {copy.company}
            </p>
            <p className="mt-1 text-xs leading-5">{copy.bin} 160740018940</p>
            <address className="mt-3 max-w-xs text-sm not-italic leading-6">
              {copy.address}
            </address>
            <div className="mt-3 flex flex-col items-start">
              <a
                href="mailto:support@glauncher.kz"
                className={`${footerLink} break-all`}
              >
                support@glauncher.kz
              </a>
              <a href="tel:+77073291065" className={footerLink}>
                +7 707 329 1065
              </a>
            </div>
            <p className="mt-1 text-xs leading-5 text-slate-500">
              {copy.hours}
            </p>
          </div>

          {groups.map((group) => (
            <nav key={group.title} aria-label={group.title} className="min-w-0">
              <h2 className="mb-3 text-sm font-semibold text-slate-900">
                {group.title}
              </h2>
              <ul>
                {group.links.map(([to, label]) => (
                  <li key={to}>
                    <Link to={to} className={footerLink}>
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>

        <div className="flex flex-col gap-5 border-t border-slate-200 py-6 lg:flex-row lg:items-center lg:justify-between lg:gap-8">
          <p className="max-w-xl text-xs leading-5 text-slate-500">
            {copy.payment}
          </p>
          <div
            className="flex flex-wrap items-center gap-x-6 gap-y-3"
            aria-label={copy.paymentSystems}
          >
            <img
              src="/visa.svg"
              alt="Visa"
              loading="lazy"
              className="h-4 w-auto object-contain"
            />
            <img
              src="/mastercard.svg"
              alt="Mastercard"
              loading="lazy"
              className="h-7 w-auto object-contain"
            />
            <a
              href="https://epayment.kz/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label={copy.halyk}
              className="flex min-h-[44px] items-center rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-goszakup-600"
            >
              <img
                src="/halyk-logo.png"
                alt="Halyk ePay"
                loading="lazy"
                className="h-6 w-auto object-contain"
              />
            </a>
          </div>
        </div>
        <p className="pb-6 text-xs leading-5 text-slate-500">
          &copy; {new Date().getFullYear()} {copy.company}. {copy.rights}
          <span className="mt-3 block"><AnalyticsPreferencesButton /></span>
        </p>
      </div>
    </footer>
  );
}
